// pages/Cashouts.tsx — FINAL RTP FULLY INTEGRATED VERSION
// Real-time sync, optimistic UI, RTP warnings, toast feedback, dollar formatting

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import { Loader2 } from 'lucide-react';
import useAdmin from '../hooks/useAdmin';
import { useConfirmModal } from '../utils/modal';
import ConfirmModal from '../../shared/components/ConfirmModal';
import { toast } from 'react-hot-toast'; // npm install react-hot-toast

interface Cashout {
  id: string;
  user_id: string;
  user_email: string;
  manager_email?: string;
  amount: number;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

interface SearchForm {
  search: string;
}

export default function Cashouts() {
  const [cashouts, setCashouts] = useState<Cashout[]>([]);
  const [loading, setLoading] = useState(false);
  const { token, fetchCashouts, handleCashout, error, setError, socket } = useAdmin();
  const { isOpen, message, showConfirmModal, handleConfirm, handleCancel } = useConfirmModal();
  const { register, watch } = useForm<SearchForm>({ defaultValues: { search: '' } });
  const search = watch('search', '');

  useEffect(() => {
    if (token) {
      setLoading(true);
      fetchCashouts()
        .then((data: Cashout[]) => {
          setCashouts(data);
          setLoading(false);
        })
        .catch((err: Error) => {
          setError(`Failed to fetch cashouts: ${err.message}`);
          setLoading(false);
        });
    }
  }, [token, fetchCashouts, setError]);

  // Real-time sync for cashouts + RTP-related events
  useEffect(() => {
    if (!socket) return;

    socket.on('cashout-updated', () => {
      toast('Cashout updated in real-time');
      fetchCashouts()
        .then(data => setCashouts((data || []) as any[]))
        .catch((err: Error) => setError(`Failed to refresh cashouts: ${err.message}`));
    });

    socket.on('rtp-updated', (data: { tableId: string; rtp: number }) => {
      toast(`RTP updated for table ${data.tableId}: ${data.rtp}% — check cashouts`);
    });

    socket.on('jackpot-win', (data: { winner: string; amount: number }) => {
      toast.success(`Jackpot win: ${data.amount} by ${data.winner} — cashouts may be affected`);
    });

    return () => {
      socket.off('cashout-updated');
      socket.off('rtp-updated');
      socket.off('jackpot-win');
    };
  }, [socket, fetchCashouts, setError]);

  const filtered = cashouts.filter(
    (c) =>
      (c.user_email?.toLowerCase()?.includes(search.toLowerCase()) || false) ||
      (c.manager_email?.toLowerCase()?.includes(search.toLowerCase()) || false)
  );

  const handleAction = (id: string, action: 'approve' | 'reject') => {
    const cashout = cashouts.find(c => c.id === id);
    if (!cashout) return;

    const actionText = action === 'approve' ? 'approve' : 'reject';
    const confirmMessage = `Are you sure you want to ${actionText} cashout of $${cashout.amount.toFixed(2)} for ${cashout.user_email}?`;

    showConfirmModal(confirmMessage, async () => {
      try {
        // Optimistic update
        setCashouts(prev =>
          prev.map(c =>
            c.id === id ? { ...c, status: action === 'approve' ? 'approved' : 'rejected' } : c
          )
        );

        await handleCashout(id, action);
        toast.success(`Cashout ${action}d successfully!`);
      } catch (err: any) {
        // Revert optimistic update on error
        setCashouts(prev =>
          prev.map(c => (c.id === id ? { ...c, status: 'pending' } : c))
        );
        setError(`Failed to ${action}: ${err.message}`);
        toast.error(`Failed to ${action}: ${err.message}`);
      }
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Pending Cashouts</h2>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">
            ✕
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <input
            type="text"
            placeholder="Search by user or manager email..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            {...register('search')}
            aria-label="Search cashouts"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-indigo-600" size={48} />
            <span className="ml-4 text-xl text-gray-700">Loading cashouts...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-gray-500 py-20">
            No cashouts found {search ? `matching "${search}"` : ''}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-4 text-left font-semibold text-gray-700">User Email</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Manager</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Amount ($)</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Status</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Requested On</th>
                  <th className="p-4 text-left font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-b hover:bg-gray-50 transition">
                    <td className="p-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.user_email || 'N/A') }} />
                    <td className="p-4 text-gray-600">{c.manager_email || 'Unassigned'}</td>
                    <td className="p-4 font-medium text-indigo-700">${c.amount.toFixed(2)}</td>
                    <td className="p-4">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                          c.status === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : c.status === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-600">
                      {c.created_at
                        ? new Date(c.created_at).toLocaleString('en-IN', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })
                        : 'N/A'}
                    </td>
                    <td className="p-4 space-x-3">
                      {c.status === 'pending' && (
                        <>
                          <button
                            className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition shadow-sm"
                            onClick={() => handleAction(c.id, 'approve')}
                            aria-label={`Approve cashout ${c.id}`}
                          >
                            Approve
                          </button>
                          <button
                            className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition shadow-sm"
                            onClick={() => handleAction(c.id, 'reject')}
                            aria-label={`Reject cashout ${c.id}`}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={isOpen}
        message={message}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
} 
